/**
 * This store contains data about the input state of the filter
 */

import * as dateFns from 'date-fns'
import * as _ from 'lodash-es'

export const useFilterInputStore = defineStore('filterInput', () => {
  const optionsStore = useOptionsStore()

  const minDate = dateFns.startOfDay(new Date(1000, 0, 1))
  const maxDate = dateFns.startOfDay(new Date())

  // Default values for all options
  // These also define the types of the options
  const defaults = {
    // View Controls

    sorting: 'family-name' as 'family-name' | 'first-name' | 'date-of-birth' | 'date-of-death',
    view: 'list' as 'grid' | 'list',

    // Generic

    search: '',

    // Personal Data

    firstName: [] as FilterData.Name[],
    familyName: [] as FilterData.Name[],
    gender: 'all' as 'all' | 'male' | 'female' | 'others',

    placeOfOrigin: [] as FilterData.Place[],
    placeOfResidence: [] as FilterData.Place[],
    placeOfBirth: [] as FilterData.Place[],
    placeOfDeath: [] as FilterData.Place[],

    dateOfBirthRange: [minDate, maxDate] as [lower: Date, upper: Date],
    dateOfDeathRange: [minDate, maxDate] as [lower: Date, upper: Date],

    // Position

    positions: [] as Array<{
      uid: string
      position: FilterMeta.Position[]
      date: [lower: Date | null, upper: Date | null]
    }>,
    partyAffiliation: [] as FilterData.Party[],

    // Occupation

    occupation: [] as FilterData.Occupation[],
    placeOfActivity: [] as FilterData.Place[],

    // Normed Data

    gndId: [] as FilterData.GND[],
    wikidataId: [] as FilterData.WikidataID[],
  }

  // Option refs
  const params = {
    // View Controls

    sorting: useParam('sortierung', defaults.sorting),
    view: useParam('ansicht', defaults.view),

    // Generic

    search: useParam('suche', defaults.search),

    // Personal Data

    firstName: useCsvParam('vorname', defaults.firstName, { headers: ['id', 'name'] }),
    familyName: useCsvParam('nachname', defaults.familyName, { headers: ['id', 'name'] }),
    gender: useParam('geschlecht', defaults.gender),

    placeOfOrigin: useCsvParam('heimatort', defaults.placeOfOrigin, { headers: ['id', 'name'] }),
    placeOfResidence: useCsvParam('wohnort', defaults.placeOfResidence, {
      headers: ['id', 'name'],
    }),
    placeOfBirth: useCsvParam('geburtsort', defaults.placeOfBirth, { headers: ['id', 'name'] }),
    placeOfDeath: useCsvParam('sterbeort', defaults.placeOfDeath, { headers: ['id', 'name'] }),

    dateOfBirthRange: useDateRangeParam('geburtsdatum', defaults.dateOfBirthRange),
    dateOfDeathRange: useDateRangeParam('sterbedatum', defaults.dateOfDeathRange),

    // Position

    positions: useCsvParam('ämter', defaults.positions, {
      flatten: value =>
        value.map(row => [
          row.date[0] ? dateFns.format(row.date[0], 'yyyy-MM-dd') : 'null',
          row.date[1] ? dateFns.format(row.date[1], 'yyyy-MM-dd') : 'null',
          ...row.position.map(position => position.label),
        ]),
      unflatten: rows =>
        rows.map((row: string[]) => ({
          uid: crypto.randomUUID(),
          position: row
            .slice(2)
            .flatMap(
              label => optionsStore.positions.find(position => position.label === label) ?? [],
            ),
          date: [
            row[0] === 'null' ? null : dateFns.parse(row[0], 'yyyy-MM-dd', new Date()),
            row[1] === 'null' ? null : dateFns.parse(row[1], 'yyyy-MM-dd', new Date()),
          ] as [Date, Date],
        })),
    }),
    partyAffiliation: useCsvParam('partei', defaults.partyAffiliation, { headers: ['id', 'name'] }),

    // Occupation

    occupation: useCsvParam('taetigkeit', defaults.occupation, { headers: ['id', 'name'] }),
    placeOfActivity: useCsvParam('wirkungsort', defaults.placeOfActivity, {
      headers: ['id', 'name'],
    }),

    // Normed Data

    gndId: useCsvParam('gnd', defaults.gndId, { headers: ['name'] }),
    wikidataId: useCsvParam('wikidata-id', defaults.wikidataId, { headers: ['name'] }),
  }

  const values = Object.fromEntries(
    Object.entries(params).map(([key, param]) => [key, param.current]),
  ) as {
    [K in keyof typeof params]: (typeof params)[K]['current']
  }

  type DecorativeOptions = 'view' | 'sorting'
  const decorativeOptions = ['view', 'sorting']

  type FilterOptionKey = keyof typeof params

  // Record of { [option]: boolean } for active filters
  const isOptionActive = computed(() => {
    const keys = Object.keys(values) as FilterOptionKey[]

    return Object.fromEntries(
      keys.map(key => {
        const defaultValue = defaults[key]
        const currentValue = values[key].value
        return [key, !_.isEqual(defaultValue, currentValue)]
      }),
    ) as Record<FilterOptionKey, boolean>
  })
  const activeOptions = computed(
    () =>
      Object.keys(isOptionActive.value).filter(
        key => isOptionActive.value[key as FilterOptionKey],
      ) as FilterOptionKey[],
  )
  const isActive = computed(
    () => activeOptions.value.filter(key => !decorativeOptions.includes(key)).length > 0,
  )

  function reset({
    include = activeOptions.value,
    exclude = decorativeOptions as DecorativeOptions[],
  }: {
    include?: readonly FilterOptionKey[]
    exclude?: readonly FilterOptionKey[]
  } = {}) {
    for (const key of include) {
      if (exclude.includes(key)) continue
      values[key].value = defaults[key]
    }
  }

  return {
    minDate,
    maxDate,

    isMinDate: markRaw((date: Date | null | undefined) =>
      date == null ? true : dateFns.isEqual(date, minDate),
    ),
    isMaxDate: markRaw((date: Date | null | undefined) =>
      date == null ? true : dateFns.isEqual(date, maxDate),
    ),

    activeOptions,
    isOptionActive,
    isActive,

    params: markRaw(params),
    values,

    reset,
  }
})
